import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Loader2, Sparkles, User, MessageCircle, Plus } from 'lucide-react';
import { aiAssistantAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const SUGGESTED_PROMPTS = [
  "What's the best programmer for a 2023 Honda Civic smart key?",
  "Customer's BMW E46 won't start after key programming, what should I check?",
  "How much should I charge for an all keys lost on a 2020 Toyota Camry?",
  "What's the difference between ID46 and ID47 chips?",
  "How do I do an OBD program on a Ford F-150 2018?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const data = await aiAssistantAPI.listSessions();
      setSessions(data || []);
    } catch (e) { /* silent */ }
  };

  const loadSession = async (sid) => {
    try {
      const data = await aiAssistantAPI.getSession(sid);
      setSessionId(sid);
      setMessages(data.messages || []);
    } catch (e) { toast.error('Failed to load'); }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput('');
  };

  const sendMessage = async (text) => {
    const msg = text || input;
    if (!msg.trim() || sending) return;

    setMessages([...messages, { role: 'user', content: msg }]);
    setInput('');
    setSending(true);

    try {
      const data = await aiAssistantAPI.chat(msg, sessionId);
      if (!sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to get response');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 fade-in h-[calc(100vh-10rem)]" data-testid="ai-assistant-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <Bot className="w-8 h-8 text-purple-500" />
          AI Locksmith Assistant
          <Sparkles className="w-6 h-6 text-yellow-500" />
        </h1>
        <p className="text-muted-foreground">Get expert advice on programming, troubleshooting, and pricing</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-full">
        {/* Sessions Sidebar */}
        <Card className="lg:col-span-1 h-fit lg:h-full">
          <CardContent className="p-3">
            <Button onClick={newChat} className="w-full btn-gradient-primary mb-3" data-testid="new-chat-button">
              <Plus className="w-4 h-4 mr-2" /> New Chat
            </Button>
            <div className="space-y-1 max-h-[500px] overflow-y-auto">
              <p className="text-xs text-muted-foreground font-semibold px-2 mb-1">Recent Chats</p>
              {sessions.length === 0 ? (
                <p className="text-xs text-muted-foreground italic px-2 py-3">No chats yet</p>
              ) : (
                sessions.map(s => (
                  <button
                    key={s.session_id}
                    onClick={() => loadSession(s.session_id)}
                    className={`w-full text-left p-2 rounded text-xs hover:bg-muted transition-colors ${sessionId === s.session_id ? 'bg-primary/20 text-primary' : ''}`}
                  >
                    <MessageCircle className="w-3 h-3 inline mr-1" />
                    {s.title || 'Untitled'}
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-3 flex flex-col h-[600px] lg:h-full">
          <CardContent className="flex-1 flex flex-col p-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <Bot className="w-16 h-16 text-purple-500/50 mb-4" />
                  <h3 className="font-bold text-lg mb-2">How can I help you today?</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">Ask me about vehicle programming, troubleshooting, pricing, or any locksmith question</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl w-full">
                    {SUGGESTED_PROMPTS.map((p, idx) => (
                      <button
                        key={idx}
                        onClick={() => sendMessage(p)}
                        className="text-left p-3 bg-muted/50 hover:bg-muted rounded-lg text-sm transition-colors border border-border/50 hover:border-primary/50"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((m, idx) => (
                    <div key={idx} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
                      {m.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className={`max-w-[80%] rounded-2xl p-3 ${
                        m.role === 'user'
                          ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white'
                          : 'bg-muted'
                      }`}>
                        <p className="text-sm whitespace-pre-line">{m.content}</p>
                      </div>
                      {m.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-blue-500" />
                        </div>
                      )}
                    </div>
                  ))}
                  {sending && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-muted rounded-2xl p-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything about locksmith work..."
                  disabled={sending}
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !input.trim()} className="btn-gradient-primary" data-testid="send-button">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
              <p className="text-[10px] text-muted-foreground mt-2 text-center">
                AI responses are guidance only. Always verify with official sources.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
